// ─────────────────────────────────────────────────────────────────────────────
// Crew — Consent gate helpers (SPINE-adjacent: pure, shared client + server).
// Owned by Agent A. UK-GDPR: granular, revocable, consent-gated-everything.
//
// These are PURE functions over a consent set. The server is the source of truth
// (it re-checks against D1 before any gated write); the client uses the same
// helpers to gate UI so the two never drift. No I/O here.
// ─────────────────────────────────────────────────────────────────────────────

import type { Consent, ConsentScope } from './types';
import { type FunnelEvent, requiresConsent } from './analytics';

// The current consent document version. Bump when the consent copy materially
// changes so re-consent can be detected (stored on each Consent row).
export const CONSENT_VERSION = '2026-06-01';

// All scopes a member can independently grant/revoke.
export const CONSENT_SCOPES: readonly ConsentScope[] = [
  'essential',
  'analytics',
  'card_link',
  'marketing',
] as const;

// `essential` is implied/always-on (service cannot operate without it) and is
// not separately revocable. Everything else defaults to NOT granted.
export const ALWAYS_ON_SCOPES: ReadonlySet<ConsentScope> = new Set(['essential']);

export type ConsentSet = ReadonlyArray<Pick<Consent, 'scope' | 'granted'>>;

// Has the member granted a given scope? `essential` is always true.
export function hasConsent(scope: ConsentScope, consents: ConsentSet): boolean {
  if (ALWAYS_ON_SCOPES.has(scope)) return true;
  const row = consents.find((c) => c.scope === scope);
  return row?.granted === true;
}

// Is recording this behavioural funnel event allowed for this member?
// Essential funnel events (group_created, member_joined) always allowed.
// Everything else needs the scope returned by requiresConsent() (== 'analytics').
export function isBehaviouralEventAllowed(event: FunnelEvent, consents: ConsentSet): boolean {
  const needed = requiresConsent(event);
  if (needed === null) return true;
  return hasConsent(needed, consents);
}

// Can we link a card for this member? Card linking is strictly card_link-gated.
export function canLinkCard(consents: ConsentSet): boolean {
  return hasConsent('card_link', consents);
}

// Can we send marketing to this member?
export function canMarket(consents: ConsentSet): boolean {
  return hasConsent('marketing', consents);
}

// Normalise a raw consent list into a complete map (missing scopes => not granted,
// except always-on scopes). Handy for rendering the consent UI and for diffing.
export function consentMap(consents: ConsentSet): Record<ConsentScope, boolean> {
  const out = {} as Record<ConsentScope, boolean>;
  for (const scope of CONSENT_SCOPES) out[scope] = hasConsent(scope, consents);
  return out;
}
